import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Settings, Eye, EyeOff, Edit2 } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { designationService } from '../services/designationService';
import { fieldConfigService } from '../services/fieldConfigService';
import APIRequest from '../api/APIRequest';
import { useSelector } from 'react-redux';

const SYSTEM_FIELDS = [
  'name', 'email', 'phone', 'dateOfBirth', 'gender', 'maritalStatus', 
  'motherTongue', 'nationality', 'aadharNumber', 'pan', 'bloodGroup', 
  'address', 'salary', 'contactPerson', 'emergencyContacts', 'subjects',
  'class', 'academicYear', 'inChargeOf', 'employeeId', 'employeeNo', 'designation', 'qualification', 'experienceInYears'
];

const StaffView = ({ teacherData: propTeacherData }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { error } = useToast();
  const [teacherData, setTeacherData] = useState(propTeacherData);
  const [designations, setDesignations] = useState([]);
  const [configFields, setConfigFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const { user } = useSelector((state) => state.auth);
  const [activeTab, setActiveTab] = useState('personal');
  const [showPassword, setShowPassword] = useState(false);

  // Fetch teacher data when component mounts (only if not passed as props)
  useEffect(() => {
    const fetchTeacherData = async () => {
      if (!id || propTeacherData) return;
      
      setLoading(true);
      try {
        const api = new APIRequest();
        const response = await api.request(`/admin/getTeacher?id=${id}`, 'GET', {}, user.token);
        if (response.success) {
          setTeacherData(response.teacher);
        } else {
          error(response.message || 'Failed to fetch teacher details');
        }
      } catch (err) {
        console.error('Error fetching teacher data:', err);
        error('Failed to fetch teacher details');
      } finally {
        setLoading(false);
      }
    };

    fetchTeacherData();
  }, [id, user.token, error, propTeacherData]);

  // Fetch designations to check fieldType
  useEffect(() => {
    const loadDesignations = async () => {
      try {
        const response = await designationService.getAllDesignations(user.token);
        const data = Array.isArray(response) ? response : (response.data || []);
        setDesignations(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error loading designations:', err);
      }
    };
    loadDesignations();
  }, [user.token]);

  useEffect(() => {
    const fetchCustomFields = async () => {
      if (!teacherData?.designation || designations.length === 0) return;
      const des = designations.find(d => d.name === teacherData.designation);
      if (des) {
        try {
          const response = await fieldConfigService.getDesignationFields(des._id, user.token);
          const fields = Array.isArray(response) ? response : (response.data || []);
          setConfigFields(fields);
        } catch (err) {
          console.error("Error fetching custom fields", err);
        }
      }
    };
    fetchCustomFields();
  }, [teacherData?.designation, designations, user.token]);

  const handleEdit = () => {
    navigate(`/staff/${id}/edit`, { state: { teacherData } });
  };

  const handleBackToList = () => {
    navigate('/staff');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  if (!teacherData) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">No staff data available</p>
      </div>
    );
  }

  const tabs = [
    { id: 'personal', label: 'Personal Information' },
    { id: 'professional', label: 'Professional Details' },
    { id: 'additional', label: 'Additional Information' }
  ];

  return (
    <div className="min-h-screen bg-slate-50/50 pb-12">
      {/* Cover Header */}
      <div className="h-48 bg-gradient-to-r from-emerald-600 to-teal-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/[0.1] bg-[size:20px_20px]" />
        <button
          onClick={handleBackToList}
          className="absolute top-6 left-6 p-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-all backdrop-blur-md flex items-center gap-2 group"
        >
          <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
          <span className="text-sm font-medium">Back</span>
        </button>
      </div>

      {/* Profile Card & Content Container */}
      <div className="max-w-7xl mx-auto px-6 -mt-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          
          {/* Left Column: Profile Card */}
          <div className="lg:col-span-4 space-y-6">
            <div className="bg-white rounded-3xl shadow-xl shadow-emerald-900/5 border border-slate-100 overflow-hidden">
              <div className="p-8 flex flex-col items-center text-center">
                <div className="relative mb-6">
                  {teacherData.image ? (
                    <img 
                      src={`/${teacherData.image}`} 
                      alt={teacherData.name} 
                      className="w-32 h-32 rounded-3xl object-cover ring-4 ring-white shadow-lg shadow-emerald-900/10"
                      onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                    />
                  ) : null}
                  <div className={`w-32 h-32 bg-gradient-to-br from-emerald-100 to-emerald-50 rounded-3xl flex items-center justify-center ring-4 ring-white shadow-lg shadow-emerald-900/10 ${teacherData.image ? 'hidden' : ''}`}>
                    <span className="text-emerald-600 font-black text-3xl">
                      {teacherData.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </span>
                  </div>
                </div>

                <h2 className="text-2xl font-bold text-slate-900 mb-1">{teacherData.name}</h2>
                <div className="space-y-2">
                  <p className="text-emerald-600 font-bold text-xs bg-emerald-50 px-3 py-1 rounded-full uppercase tracking-wider inline-block">
                    {teacherData.designation || 'Staff Member'}
                  </p>
                  <p className="text-slate-400 font-bold text-[10px] tracking-[0.2em] block uppercase">
                    ID: {teacherData.employeeId || 'N/A'}
                  </p>
                </div>

                <div className="w-full space-y-3 pt-6 mt-6 border-t border-slate-100">
                  <div className="flex justify-between items-center text-sm px-2">
                    <span className="text-slate-500 font-medium">In-Charge</span>
                    <span className="text-slate-900 font-bold">{teacherData?.class?.name || 'None'}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm px-2">
                    <span className="text-slate-500 font-medium">Experience</span>
                    <span className="text-slate-900 font-bold">{teacherData.experienceInYears} Years</span>
                  </div>
                  <div className="flex justify-between items-center text-sm px-2">
                    <span className="text-slate-500 font-medium">Gender</span>
                    <span className="text-slate-900 font-bold capitalize">{teacherData.gender}</span>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-slate-50/50 flex gap-3 border-t border-slate-100">
                <button 
                  onClick={handleEdit}
                  className="w-full bg-white hover:bg-slate-50 text-slate-700 font-bold py-3 rounded-2xl border border-slate-200 transition-all flex items-center justify-center gap-2 shadow-sm"
                >
                  <Edit2 size={16} />
                  Edit Profile
                </button>
              </div>
            </div>

            {/* Quick Contact Card */}
            <div className="bg-white rounded-3xl shadow-xl shadow-emerald-900/5 border border-slate-100 p-6 space-y-4">
              <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest pl-1">Primary Contact</h4>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center text-orange-600">
                    <Settings size={20} />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-slate-400 uppercase block">Phone</label>
                    <p className="text-slate-900 font-bold">{teacherData.phone}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-600">
                    <Settings size={20} />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-slate-400 uppercase block">Email</label>
                    <p className="text-slate-900 font-bold truncate max-w-[180px]">{teacherData.email}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Tabbed Content */}
          <div className="lg:col-span-8 space-y-8">
            {/* Tabs Header */}
            <div className="bg-white p-2 rounded-2xl shadow-xl shadow-emerald-900/5 border border-slate-100 flex gap-2 overflow-x-auto no-scrollbar">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 min-w-[140px] py-3 px-4 rounded-xl text-sm font-bold transition-all whitespace-nowrap ${
                    activeTab === tab.id 
                    ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-600/30' 
                    : 'text-slate-500 hover:bg-slate-50'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="bg-white rounded-3xl shadow-xl shadow-emerald-900/5 border border-slate-100 p-8 min-h-[500px] animate-fadeIn">
              
              {activeTab === 'personal' && (
                <div className="space-y-8">
                  {/* Mobile Login Credentials (integrated at top) */}
                  <div className="bg-emerald-50/50 p-6 rounded-3xl border border-emerald-100 flex flex-wrap items-center justify-between gap-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-2xl bg-emerald-100 flex items-center justify-center text-emerald-600">
                        <Settings size={24} />
                      </div>
                      <div>
                        <label className="text-[10px] font-black text-emerald-400 uppercase tracking-[0.2em] block mb-0.5">Mobile Login ID</label>
                        <p className="text-slate-900 font-black text-lg">{teacherData.employeeId || 'N/A'}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4 md:border-l md:border-emerald-100 md:pl-6">
                      <div>
                        <label className="text-[10px] font-black text-emerald-400 uppercase tracking-[0.2em] block mb-0.5">Password</label>
                        <div className="flex items-center gap-3">
                          <p className="text-slate-900 font-mono font-black text-lg tracking-tight">
                            {showPassword ? (teacherData.password || 'Not set') : '••••••••'}
                          </p>
                          {teacherData.password && (
                            <button
                              onClick={() => setShowPassword(!showPassword)}
                              className="p-2 text-emerald-400 hover:text-emerald-600 hover:bg-emerald-100 rounded-xl transition-all"
                              title={showPassword ? "Hide Password" : "Show Password"}
                            >
                              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="border-b border-slate-50 pb-4 pt-2">
                    <h3 className="text-xl font-bold text-slate-900">Personal Information</h3>
                    <p className="text-sm text-slate-500">Private details and contact information</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <InfoField label="Full Name" value={teacherData.name} />
                    <InfoField label="Date of Birth" value={formatDate(teacherData.dateOfBirth)} />
                    <InfoField label="Gender" value={teacherData.gender} capitalize />
                    <InfoField label="Marital Status" value={teacherData.maritalStatus} capitalize />
                    <InfoField label="Nationality" value={teacherData.nationality} />
                    <InfoField label="Mother Tongue" value={teacherData.motherTongue} />
                    <div className="md:col-span-2">
                       <InfoField label="Home Address" value={teacherData.address} />
                    </div>
                  </div>
                  
                  {teacherData.contactPerson && teacherData.contactPerson.length > 0 && (
                    <div className="pt-8 mt-8 border-t border-slate-100">
                      <h4 className="text-sm font-black text-slate-400 uppercase tracking-widest mb-6">Emergency Contacts</h4>
                      <div className="grid grid-cols-1 gap-4">
                        {teacherData.contactPerson.map((contact, index) => (
                          <div key={index} className="p-4 bg-slate-50 rounded-2xl border border-slate-100 flex items-center justify-between">
                            <div className="flex gap-4 items-center">
                              <div className="w-12 h-12 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-400 font-bold">
                                {contact.relation?.[0]}
                              </div>
                              <div>
                                <p className="text-slate-900 font-bold">{contact.name}</p>
                                <p className="text-xs text-slate-500 font-bold uppercase">{contact.relation}</p>
                              </div>
                            </div>
                            <p className="text-emerald-600 font-black">{contact.phone}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'professional' && (
                <div className="space-y-8">
                  <div className="border-b border-slate-50 pb-4">
                    <h3 className="text-xl font-bold text-slate-900">Professional Details</h3>
                    <p className="text-sm text-slate-500">Employment, qualifications and teaching scope</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <InfoField label="Current Designation" value={teacherData.designation} highlight />
                    <InfoField label="Employee ID" value={teacherData.employeeId} highlight />
                    <InfoField label="Biometric Employee No" value={teacherData.employeeNo} highlight />
                    <InfoField label="Highest Qualification" value={teacherData.qualification} />
                    <InfoField label="Years of Experience" value={`${teacherData.experienceInYears} Years`} />
                    
                    {/* Teaching Specific Fields */}
                    {designations.find(d => d.name === teacherData.designation)?.fieldType === 'teaching' && (
                      <>
                        <InfoField label="Academic Year" value={teacherData.academicYear} highlight />
                        <InfoField label="In-Charge Of" value={teacherData?.class?.name || 'Not Assigned'} highlight />
                      </>
                    )}

                    <InfoField label="PAN Number" value={teacherData.pan} />
                    <InfoField label="Aadhar Number" value={teacherData.aadharNumber} />
                    <InfoField label="Blood Group" value={teacherData.bloodGroup} />
                  </div>

                  {teacherData.subjects && teacherData.subjects.length > 0 && (
                    <div className="pt-8 border-t border-slate-100">
                      <h4 className="text-sm font-black text-slate-400 uppercase tracking-widest mb-4">Assigned Subjects</h4>
                      <div className="flex flex-wrap gap-2">
                        {teacherData.subjects.map((subject, index) => (
                          <span key={index} className="px-4 py-2 bg-emerald-50 text-emerald-700 border border-emerald-100 rounded-xl text-sm font-bold">
                            {subject.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'additional' && (
                <div className="space-y-8">
                  <div className="border-b border-slate-50 pb-4">
                    <h3 className="text-xl font-bold text-slate-900">Additional Information</h3>
                    <p className="text-sm text-slate-500">Designation-specific data and custom fields</p>
                  </div>

                  {configFields && configFields.filter(f => !(SYSTEM_FIELDS.includes(f.fieldName) && f.fieldName !== 'salary')).length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      {configFields
                        .filter(f => !(SYSTEM_FIELDS.includes(f.fieldName) && f.fieldName !== 'salary'))
                        .map(field => {
                        const isSystem = SYSTEM_FIELDS.includes(field.fieldName) || field.isSystemField;
                        let val = isSystem 
                          ? teacherData?.[field.fieldName] 
                          : teacherData?.customFields?.[field.fieldName];
                        if (field.fieldType === 'date' && val) val = formatDate(val);
                        if (typeof val === 'object' && val !== null) {
                          if (Array.isArray(val)) {
                            val = val.map(v => v?.name || v?.title || (typeof v === 'object' ? JSON.stringify(v) : v)).join(', ');
                          } else {
                            val = val.name || val.title || JSON.stringify(val);
                          }
                        }
                        return (
                          <InfoField
                            key={field._id}
                            label={field.fieldLabel}
                            value={val}
                          />
                        );
                      })}
                    </div>
                  ) : (
                    <div className="h-48 flex items-center justify-center bg-slate-50 rounded-3xl border-2 border-dashed border-slate-200">
                      <p className="text-slate-400 font-medium">No custom fields defined for {teacherData?.designation}</p>
                    </div>
                  )}
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toISOString().split('T')[0];
  } catch (e) {
    return dateString;
  }
};


// Internal component for info fields
const InfoField = ({ label, value, highlight, capitalize }) => (
  <div className="space-y-1">
    <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] block pl-1">{label}</label>
    <div className={`p-4 rounded-2xl border ${highlight ? 'bg-emerald-50/30 border-emerald-100' : 'bg-slate-50/30 border-slate-100'}`}>
      <p className={`text-slate-900 font-bold ${capitalize ? 'capitalize' : ''} ${highlight ? 'text-emerald-700' : ''}`}>
        {value || 'N/A'}
      </p>
    </div>
  </div>
);

export default StaffView;
